import { AppPermission } from "@closerplatform/spinner-openapi";
import { ChatComponentRef } from "@fidget/common/components/chat/chatComponent/chatComponent";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { staticConfig } from "@fidget/common/config/static-config";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import classNames from "classnames";
import { List, Seq } from "immutable";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { ChatInputComponent } from "../../../containers/chat/chatInputComponent";
import { EmptyChatComponent } from "../../../containers/chat/emptyChatComponent";
import { useStore } from "../../../models";
import { useAgentServices } from "../../../models/agentServices";
import { InboxFilterId } from "../../../models/inbox";
import { ChatItemAssertion } from "../../../models/room/chat";
import { IChatItem } from "../../../models/room/chat/ChatItem";
import { INoteChatItem } from "../../../models/room/chat/items";
import { AgentChat } from "../../chat/AgentChat";
import { ChatNote } from "../../chat/chatNote";
import { ProfileComponent } from "../../profile/profileComponent";
import { TagsList } from "../../settings/tags/tagName/tagsList";
import { AssigneeControl } from "../assignee/assigneeControl";
import { DeleteClient } from "../deleteClient";
import { FollowControl } from "../followControl/followControl";
import { FollowList } from "../followList/followList";
import { NotesLimit } from "../notesLimit";
import { RichMessages } from "../richMessages/richMessages";
import { StateButton } from "../stateButton";
import { TopicsSummary } from "../topicsSummary/topicsSummary";
import { ClientScrollSection } from "./clientScrollSection/clientScrollSection";
import styles from "./currentClientContainer.css";
import { OmniButton } from "../OnmiButton/OmniButton";

interface Props {
  inboxFilter?: InboxFilterId;
  roomId?: string;
}

export const CurrentClientContainer = observer(({ inboxFilter, roomId }: Props) => {
  const { roomStore, tagsStore, uiStore, topicStore, tagGroupsStore } = useStore();
  const { artichokeService } = useAgentServices();
  const [showAllNotes, setShowAllNotes] = useState(false);
  const currentRoom = roomStore.currentRoom;
  const scrollContainer = useRef<ChatComponentRef>();

  useEffect(() => {
    if (roomId) {
      roomStore.setCurrentRoom(roomId);
    }
  }, [roomId]);

  const toggleShowAllNotes = () => {
    setShowAllNotes(!showAllNotes);
  };

  // FIXME: this won't get all notes if some are outside the currently loaded range
  const getNotes = (): List<INoteChatItem> => {
    if (currentRoom) {
      return Seq(
        currentRoom.chat.chatItems.filter((item: IChatItem) => ChatItemAssertion.isNote(item))
      ).toList();
    }
    return List<INoteChatItem>();
  };

  const renderNotes = (notes: List<INoteChatItem>) => {
    const areAllNotesShown = notes.size <= staticConfig.notesHiddenSize || showAllNotes;
    const showUpgradeButton = roomStore.currentRoom!.chat.hasFake && areAllNotesShown;
    return (
      <div className={styles.notes}>
        <div className={styles["notes-container"]}>
          {notes
            .reverse()
            .slice(0, showAllNotes ? notes.size : staticConfig.notesHiddenSize)
            .map((note) => {
              return (
                <div key={note.id} className={styles.note}>
                  <ChatNote {...note} compactMode />
                </div>
              );
            })
            .toArray()}
          {showUpgradeButton && <NotesLimit />}
        </div>
        {notes.size > staticConfig.notesHiddenSize && (
          <AgentButton
            icon={"icon-show-more"}
            buttonStyle={"transparent secondary"}
            onClick={toggleShowAllNotes}
          >
            {showAllNotes ? (
              <L10n id={"conversation.notes.hide"} />
            ) : (
              <L10n
                id={"conversation.notes.showMore"}
                values={{ count: notes.size - staticConfig.notesHiddenSize }}
              />
            )}
          </AgentButton>
        )}
      </div>
    );
  };

  const renderCurrentClient = () => {
    if (currentRoom) {
      return (
        <div className={styles["current-client-chat"]}>
          <div className={styles.header}>
            <div className={styles["name-container"]}>
              <div
                className={classNames(styles["room-label"], {
                  [styles["random-name"]]:
                    !currentRoom.owner.firstName && !currentRoom.owner.lastName
                })}
              >
                {currentRoom.owner.displayedName}
              </div>
              {inboxFilter === InboxFilterId.YOURS && (
                <button
                  className={classNames(styles["snooze-button"], {
                    [styles.snoozed]: currentRoom.isSnoozed
                  })}
                  onClick={currentRoom.toggleSnooze}
                >
                  <i className={"icon-snooze"} />
                </button>
              )}
            </div>
            <AssigneeControl room={currentRoom} />
          </div>
          <AgentChat
            scrollContainerRef={scrollContainer}
            containerClassName={styles["chat-box"]}
            room={currentRoom}
          />
          <ChatInputComponent
            inputState={currentRoom.chatInput}
            onMessage={(msg, attachments) => {
              artichokeService.indicateTypingThrottled.cancel();
              currentRoom.sendMessage(msg, attachments);
              scrollContainer.current?.scrollToBottom();
            }}
            onNote={(note) => currentRoom.sendNote(note)}
            onMessageTyping={() => artichokeService.indicateTypingThrottled(currentRoom.id)}
            guestFirstName={currentRoom.owner.firstName}
          />
        </div>
      );
    }
    return (
      <div className={styles["current-client-chat"]}>
        <div className={styles.header} />
        <div className={styles["chat-box"]} />
        <div className={styles["chat-input"]} />
      </div>
    );
  };

  const render = () => {
    if (!currentRoom) {
      return <EmptyChatComponent />;
    }

    const tags = tagsStore.filterDisplayableTags(currentRoom?.owner.tags.slice());

    const canDeleteClient =
      uiStore.canDisplayWithPermission(AppPermission.LeadDeleteProfile) &&
      currentRoom !== undefined &&
      currentRoom.owner.deletedAt === undefined;
    const canCloseConversation =
      !!currentRoom?.isAssignedToMe ||
      uiStore.canDisplayWithPermission(AppPermission.RoomCloseNotOwn);
    const notes = getNotes();
    const tagGroupId = currentRoom.owner.tagGroupId;
    const tagGroup = tagGroupId ? tagGroupsStore.getById(tagGroupId) : undefined;
    const tagGroups = tagGroup && tagGroup.showInConversationDetails ? [tagGroup] : [];
    const guestTopics = currentRoom.owner.topics
      ? topicStore.getByNames(currentRoom.owner.topics)
      : [];
    const topics = tagGroupId ? topicStore.getTagGroupTopics(tagGroupId) : topicStore.getTopics();

    const showTags = (tags && tags.length > 0) || tagGroups.length > 0;

    return (
      <div className={styles["current-client"]}>
        {renderCurrentClient()}

        <div className={styles["current-client-profile"]}>
          {canCloseConversation ? (
            <StateButton room={currentRoom} />
          ) : (
            <div className={styles["close-placeholder"]} />
          )}
          <OmniButton />
          <div className={styles["current-client-profile-scroll"]}>
            <ClientScrollSection label={intl.formatMessage("richMessages.header")}>
              <RichMessages room={currentRoom} />
            </ClientScrollSection>
            <ClientScrollSection label={intl.formatMessage("profile.title")}>
              <ProfileComponent room={currentRoom} />
            </ClientScrollSection>
            {(!!topics.length || !!guestTopics.length) && (
              <ClientScrollSection label={intl.formatMessage("topics")}>
                <TopicsSummary
                  assignableTopics={topics}
                  assignedTopics={guestTopics}
                  onChange={(updatedTopics) =>
                    currentRoom.updateTopics(updatedTopics.map((t) => t.name))
                  }
                />
              </ClientScrollSection>
            )}
            <ClientScrollSection label={intl.formatMessage("tags.title")}>
              {showTags ? (
                <TagsList tags={tags} tagGroups={tagGroups} />
              ) : (
                <L10n id="tags.noAvailable" />
              )}
            </ClientScrollSection>
            <ClientScrollSection
              label={intl.formatMessage("conversation.followers")}
              LabelAction={!currentRoom.isAssignedToMe && <FollowControl room={currentRoom} />}
            >
              <FollowList room={currentRoom} />
            </ClientScrollSection>
            {notes && !notes.isEmpty() && (
              <ClientScrollSection label={intl.formatMessage("conversation.notes.title")}>
                {renderNotes(notes)}
              </ClientScrollSection>
            )}
            {canDeleteClient && <DeleteClient room={currentRoom} />}
          </div>
        </div>
      </div>
    );
  };
  return render();
});
